'use client';

// noinspection ExceptionCaughtLocallyJS
/* eslint-disable no-continue, no-param-reassign, no-underscore-dangle,
  react/jsx-no-constructed-context-values,
  @typescript-eslint/consistent-type-definitions,
  @typescript-eslint/no-explicit-any,
  @typescript-eslint/no-floating-promises,
  @typescript-eslint/no-misused-promises,
  @typescript-eslint/no-unsafe-assignment,
  @typescript-eslint/no-unsafe-call,
  @typescript-eslint/no-unsafe-member-access,
  @typescript-eslint/no-unsafe-return,
  @typescript-eslint/no-use-before-define */

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { monarch } from '@highereducation/cohesion-utils-react';
import monarchConfig from '../../../../helpers/constants/monarchConfig';
import { cohesion } from '../../../../utils/eventing/api';

interface RuleState<T = unknown> {
  isLoading: boolean;
  value: T;
}

type State = Record<string, RuleState>;

interface RequestStartAction {
  type: 'REQUEST_START';
  key: string;
}

interface RequestSuccessAction {
  type: 'REQUEST_SUCCESS';
  key: string;
  value: unknown;
}

interface RequestFailureAction {
  type: 'REQUEST_FAILURE';
  key: string;
}

type Action = RequestStartAction | RequestSuccessAction | RequestFailureAction;

const serializeError = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }

  try {
    const serialized = JSON.stringify(error);
    return serialized && serialized !== '{}' ? serialized : String(error);
  } catch {
    return String(error);
  }
};

// Define types for the context
interface MonarchRuleContextType {
  state: State;
  fetchRule: (
    ruleName: string,
    params: Record<string, unknown> | undefined,
  ) => void;
}

const MonarchRuleContext = createContext<MonarchRuleContextType | undefined>(
  undefined,
);

// https://github.com/RedVentures/cohesion-client-js/blob/3d1b5ee33e1c8275d2c58977206eda44759a0164/src/libs/monarch/utils.js#L69
export function updatePayloadWithPreviews(payload: Record<string, any>): void {
  const url = new URL(window.location.href);
  const monarchPreviewString = url.searchParams.get('monarchPreview');

  const isStrict = url.searchParams.get('monarchPreviewStrict') === '1';
  const previewTuples = monarchPreviewString
    ? monarchPreviewString.split(';').map((s) => s.split('.'))
    : [];
  const previews: Record<string, string> = {};
  for (let i = 0; i < previewTuples.length; i++) {
    const previewTuple = previewTuples[i];
    if (i === 0 && previewTuple.length === 1) {
      // Allow the first tuple to implicitly be for the `trafficFlow`.
      previewTuple.unshift('trafficFlow');
    } else if (previewTuple.length !== 2) {
      // Otherwise, require all tuples to have exactly two parts.
      continue;
    }
    const [ruleId, returnValueId] = previewTuple;
    previews[ruleId] = returnValueId;
  }
  if (Object.keys(previews).length !== 0) {
    payload._Monarch ??= {};
    payload._Monarch.forceReturns ??= {};
    payload._Monarch.forceReturns = {
      ...payload._Monarch.forceReturns,
      ...previews,
    };
    if (!isStrict) {
      payload._Monarch.allowInvalidForcedReturns = true;
    }
  }
  const monarchDraftPreviewString = url.searchParams.get('monarchDraft');
  const draftPreviews = monarchDraftPreviewString
    ? monarchDraftPreviewString.split(';')
    : [];
  if (draftPreviews.length !== 0) {
    payload._Monarch ??= {};
    payload._Monarch.draftPreviewIds ??= [];
    payload._Monarch.draftPreviewIds = [
      ...payload._Monarch.draftPreviewIds,
      ...draftPreviews,
    ];
  }
}

// Reducer function
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'REQUEST_START':
      return {
        ...state,
        [action.key]: { isLoading: true, value: undefined },
      };
    case 'REQUEST_SUCCESS':
      return {
        ...state,
        [action.key]: { isLoading: false, value: action.value },
      };
    case 'REQUEST_FAILURE':
      return {
        ...state,
        [action.key]: { isLoading: false, value: undefined },
      };
    default:
      return state;
  }
};

interface MonarchRuleProviderProps {
  children: ReactNode;
}

export const MonarchRuleProvider = ({ children }: MonarchRuleProviderProps) => {
  const [state, dispatch] = useReducer(reducer, {});
  const initialized = useRef(false);
  const queue = useRef<
    { ruleName: string; params: Record<string, unknown> | undefined }[]
  >([]);

  const processQueue = (
    cb: (params: {
      ruleName: string;
      params: Record<string, unknown> | undefined;
    }) => void,
  ) => {
    const uniqueQueue = Array.from(
      new Set(
        queue.current.map(({ ruleName, params }) =>
          JSON.stringify({ ruleName, params }),
        ),
      ),
    ).map((item) => JSON.parse(item));

    uniqueQueue.forEach(cb);
  };

  useEffect(() => {
    if (initialized.current) {
      return;
    }

    setTimeout(() => {
      if (!initialized.current) {
        processQueue(({ ruleName, params }) => {
          dispatch({
            type: 'REQUEST_FAILURE',
            key: JSON.stringify({ ruleName, params }),
          });
        });
      }
    }, 2000);

    // Listen for cohesion ready event
    cohesion('ready', () => {
      initialized.current = true;
      processQueue(({ ruleName, params }) => {
        fetchRule(ruleName, params);
      });

      queue.current = [];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monarchApiClient = (
    ruleId: string,
    payload?: Record<string, unknown>,
  ) =>
    new Promise((resolve, reject) => {
      // Add timeout to prevent infinite hanging
      const timeout = setTimeout(() => {
        reject(new Error('Monarch request timed out'));
      }, 10000);

      monarch(
        'rule',
        ruleId,
        payload ?? {},
        { sourceId: monarchConfig.sourceId },
        (err, result) => {
          clearTimeout(timeout);

          if (err) {
            // Ensure we're rejecting with an Error object. String(err) on a
            // plain object yields "[object Object]", losing the reason, so
            // serialize non-Error rejections instead.
            reject(err instanceof Error ? err : new Error(serializeError(err)));
          } else {
            resolve(result);
          }
        },
      );
    });

  const fetchRule = async (
    ruleName: string,
    params?: Record<string, unknown>,
  ) => {
    const key = JSON.stringify({ ruleName, params });

    if (state[key]?.isLoading) {
      return;
    }
    if (!initialized.current) {
      queue.current.push({ ruleName, params });
      return;
    }

    dispatch({ type: 'REQUEST_START', key });

    try {
      // Check if monarch is ready before making the call
      if (typeof window.monarch !== 'function') {
        throw new Error('Monarch function not available');
      }

      const result = await monarchApiClient(ruleName, params ?? {});

      if (!result) {
        throw new Error('Monarch response was not ok');
      }

      dispatch({
        type: 'REQUEST_SUCCESS',
        key,
        value: (result as any)[ruleName],
      });
    } catch (error) {
      window.newrelic?.noticeError(error as Error, {
        monarchRuleName: ruleName,
      });
      console.error('Failed to fetch Monarch rule:', error);
      dispatch({ type: 'REQUEST_FAILURE', key });
    }
  };

  return (
    <MonarchRuleContext.Provider value={{ state, fetchRule }}>
      {children}
    </MonarchRuleContext.Provider>
  );
};

export function useMonarchRule<T>(
  ruleName: string,
  params?: Record<string, unknown>,
): RuleState<T> {
  const context = useContext(MonarchRuleContext);
  if (!context) {
    throw new Error('useMonarchRule must be used within a MonarchRuleProvider');
  }

  const { state, fetchRule } = context;
  const key = JSON.stringify({ ruleName, params });

  // Store the monarch enabled state
  const isMonarchDisabled = () => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      return url.searchParams.get('monarchEnabled') === 'false';
    }
    return false;
  };

  useEffect(() => {
    // Skip fetching if monarch is disabled via query param
    if (isMonarchDisabled()) {
      return;
    }

    if (!state[key]) {
      fetchRule(ruleName, params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ruleName, params, state, isMonarchDisabled]);

  // Return early with non-loading state if monarch is disabled
  if (isMonarchDisabled()) {
    return {
      isLoading: false,
      value: undefined as unknown as T,
    };
  }

  return (
    (state[key] as unknown as RuleState<T>) ?? {
      isLoading: true,
      value: undefined,
    }
  );
}
